import { useState } from 'react'
import AgeGroupSelector from '../../components/AgeGroupSelector'
import MathProblemSet from './MathProblemSet'
import { generateProblems, correctAnswer } from '../../lib/math/generator'
import {
  AgeGroup,
  MathConfig,
  MathLayout,
  MathOperation,
  MathProblem,
  MathProblemCount,
  MATH_DEFAULTS,
  MATH_OPERATION_SYMBOLS,
} from '../../types'

// ─── Config defaults ──────────────────────────────────────────────────────────

const defaultConfig: MathConfig = {
  ageGroup:     'young',
  operations:   ['addition', 'subtraction'],
  resultMin:    1,
  resultMax:    20,
  problemCount: 20,
  layout:       'grid',
}

const ALL_OPERATIONS: MathOperation[] = ['addition', 'subtraction', 'multiplication', 'division']
const PROBLEM_COUNTS: MathProblemCount[] = [10, 20, 30, 50]
const LAYOUTS: { value: MathLayout; label: string; icon: string }[] = [
  { value: 'list', label: 'List', icon: '☰' },
  { value: 'grid', label: 'Grid', icon: '⊞' },
]

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function MathPage() {
  const [config, setConfig]       = useState<MathConfig>(defaultConfig)
  const [problems, setProblems]   = useState<MathProblem[] | null>(null)
  const [answers, setAnswers]     = useState<string[]>([])
  const [checked, setChecked]         = useState(false)
  const [generating, setGenerating]   = useState(false)
  const [withSolution, setWithSolution] = useState(false)
  const [printing, setPrinting]         = useState(false)
  const [worksheetCount, setWorksheetCount] = useState(1)

  async function handlePrint() {
    if (!problems) return
    setPrinting(true)
    try {
      const { createElement }   = await import('react')
      const { downloadPDF }     = await import('../../lib/pdf/download')
      const { MathDocument }    = await import('../../lib/pdf/math')
      const { generateProblems: gen } = await import('../../lib/math/generator')
      const problemSets = [
        problems,
        ...Array.from({ length: worksheetCount - 1 }, () => gen(config)),
      ]
      const filename = worksheetCount > 1
        ? `math-worksheet-${worksheetCount}x-${config.problemCount}-problems.pdf`
        : `math-puzzle-${config.problemCount}-problems.pdf`
      await downloadPDF(createElement(MathDocument, { problemSets, config, withSolution }), filename)
    } finally {
      setPrinting(false)
    }
  }

  // ── Derived ────────────────────────────────────────────────────────────

  const score = problems
    ? problems.filter((p, i) => {
        const n = parseInt(answers[i] ?? '', 10)
        return !isNaN(n) && n === correctAnswer(p)
      }).length
    : 0

  const filled  = answers.filter((a) => a !== '' && a !== undefined).length
  const perfect = checked && problems !== null && score === problems.length

  // ── Handlers ──────────────────────────────────────────────────────────

  function handleStart(cfg: MathConfig) {
    setGenerating(true)
    setTimeout(() => {
      const ps = generateProblems(cfg)
      setProblems(ps)
      setAnswers(new Array(ps.length).fill(''))
      setChecked(false)
      setGenerating(false)
    }, 20)
  }

  function handleAnswer(idx: number, value: string) {
    setAnswers((prev) => {
      const next = [...prev]
      next[idx] = value
      return next
    })
    // Reset check when user edits
    if (checked) setChecked(false)
  }

  function handleCheck() {
    setChecked(true)
  }

  function handleNewSet() {
    handleStart(config)
  }


  // ── Screens ───────────────────────────────────────────────────────────

  if (!problems) {
    return (
      <ConfigScreen
        config={config}
        generating={generating}
        onAgeGroupChange={(ageGroup: AgeGroup) => {
          const defaults = MATH_DEFAULTS[ageGroup]
          setConfig((p) => ({ ...p, ageGroup, ...defaults }))
        }}
        onToggleOperation={(op: MathOperation) =>
          setConfig((p) => {
            const has = p.operations.includes(op)
            if (has && p.operations.length === 1) return p
            return {
              ...p,
              operations: has ? p.operations.filter((o) => o !== op) : [...p.operations, op],
            }
          })
        }
        onResultMinChange={(v: number) => setConfig((p) => ({ ...p, resultMin: v }))}
        onResultMaxChange={(v: number) => setConfig((p) => ({ ...p, resultMax: v }))}
        onProblemCountChange={(v: MathProblemCount) => setConfig((p) => ({ ...p, problemCount: v }))}
        onLayoutChange={(v: MathLayout) => setConfig((p) => ({ ...p, layout: v }))}
        onStart={() => handleStart(config)}
      />
    )
  }

  return (
    <div>
      {/* ── Header ── */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-display font-black text-2xl text-bubble-text">Math Puzzle</h1>
          <p className="text-sm text-bubble-muted">
            {problems.length} problems · {config.operations.map((o) => MATH_OPERATION_SYMBOLS[o]).join(' ')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleNewSet}
            disabled={generating}
            className="btn-secondary text-sm px-4 py-2"
          >
            {generating ? '⏳' : '↺'} New Set
          </button>
          <label className="flex items-center gap-1 text-xs text-bubble-muted font-black cursor-pointer select-none">
            <input
              type="checkbox"
              checked={withSolution}
              onChange={(e) => setWithSolution(e.target.checked)}
              className="accent-bubble-coral"
            />
            +solution
          </label>
          <select
            value={worksheetCount}
            onChange={(e) => setWorksheetCount(Number(e.target.value))}
            className="text-xs font-black text-bubble-text border-2 border-gray-200 rounded-full px-3 py-1.5 bg-white cursor-pointer focus:outline-none focus:border-bubble-coral"
            aria-label="Number of pages to print"
          >
            {[1, 2, 3, 5, 10].map((n) => (
              <option key={n} value={n}>{n === 1 ? '1 page' : `${n} pages`}</option>
            ))}
          </select>
          <button
            onClick={handlePrint}
            disabled={printing}
            className="btn-primary text-sm px-4 py-2 disabled:opacity-60"
          >
            {printing ? '⏳ Generating…' : `🖨 ${worksheetCount > 1 ? 'Worksheet' : 'Print'}`}
          </button>
        </div>
      </div>

      {/* ── Score banner (after checking) ── */}
      {checked && (
        <div
          className={`card mb-5 p-4 text-center ${
            perfect
              ? 'border-bubble-teal bg-bubble-teal-light'
              : 'border-bubble-pink bg-bubble-pink-light'
          }`}
        >
          {perfect ? (
            <>
              <p className="font-display font-black text-xl text-teal-700 mb-1">
                🎉 Perfect score!
              </p>
              <p className="text-sm text-teal-600">
                {problems.length} / {problems.length} correct — brilliant!
              </p>
            </>
          ) : (
            <>
              <p className="font-display font-black text-xl text-bubble-pink mb-1">
                {score} / {problems.length} correct
              </p>
              <p className="text-sm text-pink-600">
                {score === 0
                  ? 'Keep trying — you\'ve got this!'
                  : `${problems.length - score} to fix — almost there!`}
              </p>
            </>
          )}
        </div>
      )}

      {/* ── Progress (before checking) ── */}
      {!checked && problems.length > 0 && (
        <div className="mb-4">
          <div className="flex justify-between text-xs text-bubble-muted font-black mb-1">
            <span>{filled} / {problems.length} filled</span>
            <span>{Math.round((filled / problems.length) * 100)}%</span>
          </div>
          <div className="h-2 bg-bubble-pink-light rounded-full overflow-hidden">
            <div
              className="h-full bg-bubble-pink rounded-full transition-all duration-300"
              style={{ width: `${(filled / problems.length) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* ── Problems ── */}
      <MathProblemSet
        problems={problems}
        answers={answers}
        checked={checked}
        layout={config.layout}
        onAnswer={handleAnswer}
      />

      {/* ── Actions ── */}
      <div className="flex gap-3 mt-6 flex-wrap">
        <button
          onClick={handleCheck}
          disabled={filled === 0}
          className="btn-primary px-6 py-2.5 text-sm disabled:opacity-50"
        >
          Check Answers
        </button>
        {checked && !perfect && (
          <button
            onClick={() => setChecked(false)}
            className="btn-secondary px-6 py-2.5 text-sm"
          >
            Keep trying
          </button>
        )}
        {checked && (
          <button
            onClick={handleNewSet}
            className="btn-ghost px-6 py-2.5 text-sm"
          >
            New set →
          </button>
        )}
      </div>
    </div>
  )
}

// ─── Config screen ────────────────────────────────────────────────────────────

interface ConfigScreenProps {
  config:                MathConfig
  generating:            boolean
  onAgeGroupChange:      (v: AgeGroup) => void
  onToggleOperation:     (v: MathOperation) => void
  onResultMinChange:     (v: number) => void
  onResultMaxChange:     (v: number) => void
  onProblemCountChange:  (v: MathProblemCount) => void
  onLayoutChange:        (v: MathLayout) => void
  onStart:               () => void
}

function ConfigScreen({
  config,
  generating,
  onAgeGroupChange,
  onToggleOperation,
  onResultMinChange,
  onResultMaxChange,
  onProblemCountChange,
  onLayoutChange,
  onStart,
}: ConfigScreenProps) {
  return (
    <div className="max-w-lg">
      <h1 className="font-display font-black text-3xl text-bubble-text mb-1">Math Puzzle</h1>
      <p className="text-bubble-muted mb-8">Configure your arithmetic challenge.</p>

      <div className="card p-6 space-y-6">
        <AgeGroupSelector value={config.ageGroup} onChange={onAgeGroupChange} />

        {/* Operations */}
        <div>
          <label className="config-label">Operations</label>
          <div className="flex gap-2" role="group" aria-label="Operations">
            {ALL_OPERATIONS.map((op) => {
              const active = config.operations.includes(op)
              return (
                <button
                  key={op}
                  aria-pressed={active}
                  onClick={() => onToggleOperation(op)}
                  className={active ? 'sel-btn-active' : 'sel-btn'}
                >
                  <span className="text-xl font-black" aria-hidden>
                    {MATH_OPERATION_SYMBOLS[op]}
                  </span>
                  <span className="text-xs mt-0.5 capitalize">{op.slice(0, 4)}</span>
                </button>
              )
            })}
          </div>
        </div>

        {/* Result range */}
        <div>
          <label className="config-label">Answer Range</label>
          <div className="flex items-center gap-3">
            <div className="flex-1">
              <label className="text-xs text-bubble-muted mb-1 block font-black">Min</label>
              <input
                type="number"
                min={0}
                max={config.resultMax - 1}
                value={config.resultMin}
                onChange={(e) =>
                  onResultMinChange(Math.max(0, parseInt(e.target.value) || 0))
                }
                className="w-full rounded-[16px] border-2 border-gray-200 px-3 py-2 font-black
                           text-center focus:outline-none focus:border-bubble-coral
                           [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none
                           [&::-webkit-outer-spin-button]:appearance-none"
              />
            </div>
            <span className="text-bubble-muted mt-4 font-black">–</span>
            <div className="flex-1">
              <label className="text-xs text-bubble-muted mb-1 block font-black">Max</label>
              <input
                type="number"
                min={config.resultMin + 1}
                max={9999}
                value={config.resultMax}
                onChange={(e) =>
                  onResultMaxChange(
                    Math.max(config.resultMin + 1, parseInt(e.target.value) || config.resultMin + 1),
                  )
                }
                className="w-full rounded-[16px] border-2 border-gray-200 px-3 py-2 font-black
                           text-center focus:outline-none focus:border-bubble-coral
                           [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none
                           [&::-webkit-outer-spin-button]:appearance-none"
              />
            </div>
          </div>
        </div>

        {/* Problem count */}
        <div>
          <label className="config-label">Number of Problems</label>
          <div className="flex gap-2" role="radiogroup" aria-label="Problem count">
            {PROBLEM_COUNTS.map((n) => (
              <button
                key={n}
                role="radio"
                aria-checked={config.problemCount === n}
                onClick={() => onProblemCountChange(n)}
                className={config.problemCount === n ? 'sel-btn-active' : 'sel-btn'}
              >
                {n}
              </button>
            ))}
          </div>
        </div>

        {/* Layout */}
        <div>
          <label className="config-label">Layout</label>
          <div className="flex gap-2" role="radiogroup" aria-label="Layout">
            {LAYOUTS.map(({ value, label, icon }) => (
              <button
                key={value}
                role="radio"
                aria-checked={config.layout === value}
                onClick={() => onLayoutChange(value)}
                className={`${config.layout === value ? 'sel-btn-active' : 'sel-btn'} flex-row gap-2`}
              >
                <span aria-hidden>{icon}</span>
                {label}
              </button>
            ))}
          </div>
        </div>

        <button
          onClick={onStart}
          disabled={generating || config.operations.length === 0}
          className="btn-primary w-full text-base py-3 disabled:opacity-50"
        >
          {generating ? 'Generating…' : 'Generate Problems →'}
        </button>
      </div>
    </div>
  )
}
